import React, { useContext, useEffect, useState } from 'react'
import * as yup from 'yup'
import { useCallbackOnce } from '@zuto/utilities'
import MuiTextField from '@zuto/atoms/src/Mui/TextField/MuiTextField'
import { EmployerDetailsEntry, EmploymentEntryErrors } from '../EmploymentHistoryContext'
import LengthOfTimeEntry from '../../../LengthOfTime/LengthOfTimeEntry'
import AppFormQuestion from '../../../AppFormQuestion'
import QuestionKey from '../../../../Questions/question-key'
import { ApplicationFormContext } from '../../../ApplicationForm/ApplicationFormContext'
import withFade from '../../../HOC/withFade'
import NavigationButtons from '../../../NavigationButtons/NavigationButtons'
import EmploymentHistoryEntryContext from '../EmploymentEntry/EmploymentHistoryEntryContext'
import EmploymentHistoryValidator from '../../../../validators/EmploymentHistoryValidator'
import { questionFontStyle, fireReactGAEvent } from '../../utils'
import { HistoryIndex } from '../../HistoryEntry/HistoryEntry'
import LengthOfTime from '../../../LengthOfTime'
import { GridItem } from '@zuto/core/src/grid'

interface EmployerDetailsProps {
  existingAnswers?: EmployerDetailsState
  entryId: HistoryIndex
  disablePrevious?: boolean
  includeTimeInSituationFields: boolean
  areEmploymentDetailsRequired?: boolean
}

interface EmployerDetailsState {
  employerDetails?: EmployerDetailsEntry
  lengthOfTime?: LengthOfTimeEntry
}

type Errors = {
  message?: string,
  fields: string[]
}

const EmployerDetails = ({
  existingAnswers = {},
  entryId,
  disablePrevious = false,
  includeTimeInSituationFields,
  areEmploymentDetailsRequired = true
}: EmployerDetailsProps): JSX.Element => {
  const { dispatch } = useContext(EmploymentHistoryEntryContext)
  const { dispatch: appFormContextDispatch } = useContext(ApplicationFormContext)
  const [employerDetailsState, setEmployerDetailsState] = useState<EmployerDetailsState>(existingAnswers)
  const [errors, setErrors] = useState<EmploymentEntryErrors>({})
  const [nextButtonCb, resetNextButtonCb] = useCallbackOnce()
  const [previousButtonCb] = useCallbackOnce()

  const updateEmployerDetailsState = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget

    setEmployerDetailsState((prev) => ({
      ...prev,
      employerDetails: {
        ...(prev.employerDetails as Record<string, unknown>),
        [name]: value.trim()
      }
    }))
  }

  const updateLengthOfTimeState = (value: Record<string, unknown>) => {
    setEmployerDetailsState((prev) => ({
      ...prev,
      lengthOfTime: {
        ...(prev.lengthOfTime as Record<string, unknown>),
        ...value
      }
    }))
  }

  useEffect(() => {
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Question Visible',
      label: `qst.employmentHistory.${parseInt(entryId, 10) - 1}.employerDetails`,
      nonInteraction: true
    })
  }, [])

  // when employmentRedesignV1 and employmentRedesignV1_1,
  // as areEmploymentDetailsRequired is only passed in those cases
  useEffect(() => {
    if (!areEmploymentDetailsRequired) {
      // delete employer details when user changes status in dropdown
      // and new status doesnt require employer details
      setEmployerDetailsState({
        ...employerDetailsState,
        employerDetails: {}
      })
    }
  }, [areEmploymentDetailsRequired])

  const getEmployeeDetailsValues = (entry: string): string => {
    const employeeDetails = employerDetailsState.employerDetails as Record<string, unknown>
    if (!employeeDetails) {
      return ''
    }
    return employeeDetails[entry] as string
  }

  const lengthOfTimeErrors = (): Errors => {
    const monthsError = errors?.months
    const yearsError = errors?.years
    const durationError = errors?.duration
    const errorObject: Errors = {
      message: monthsError || yearsError || durationError,
      fields: []
    }
    if (monthsError) {
      errorObject.fields.push('months')
    }
    if (yearsError) {
      errorObject.fields.push('years')
    }
    if (durationError) {
      errorObject.fields.push('duration')
    }

    return errorObject
  }

  const hasEnoughInfoToProceed = () => {
    const jobTitleFilled = employerDetailsState.employerDetails?.jobTitle
    const companyNameFilled = employerDetailsState.employerDetails?.companyName
    const lengthOfTimeFilled = employerDetailsState.lengthOfTime?.years || employerDetailsState.lengthOfTime?.months

    const employerDetailsFilled = jobTitleFilled && companyNameFilled

    return employerDetailsFilled && (!includeTimeInSituationFields || lengthOfTimeFilled)
  }

  const employerDetailsValidator = EmploymentHistoryValidator.EmploymentDetailsValidator(
    includeTimeInSituationFields
  ).getValidator()

  const validate = (): boolean => {
    const schema = employerDetailsValidator
    let objectToValidate: Record<string, string | undefined> = {}
    if (areEmploymentDetailsRequired) {
      objectToValidate = {
        ...objectToValidate,
        jobTitle: employerDetailsState.employerDetails?.jobTitle,
        companyName: employerDetailsState.employerDetails?.companyName
      }
    }
    if (includeTimeInSituationFields) {
      objectToValidate = {
        ...objectToValidate,
        years: employerDetailsState.lengthOfTime?.years,
        months: employerDetailsState.lengthOfTime?.months
      }
    }
    try {
      schema.validateSync(objectToValidate, {
        abortEarly: false
      })
    } catch (e: unknown) {
      const err = e as yup.ValidationError
      const prettyErrors: Record<string, string> = {}
      if (!err.inner) return false
      err.inner.forEach((validationError) => {
        if (!validationError.path) return
        const [errorMessage] = validationError.errors
        prettyErrors[validationError.path.toString()] = errorMessage
        fireReactGAEvent({
          category: 'Application Form',
          action: 'Validation Error',
          label: validationError.path.toString(),
          nonInteraction: true
        })
      })
      setErrors(prettyErrors)
      return false
    }

    return true
  }

  const saveValues = () => {
    dispatch({
      type: 'UPDATE_EMPLOYER_DETAILS',
      value: {
        jobTitle: employerDetailsState.employerDetails?.jobTitle,
        companyName: employerDetailsState.employerDetails?.companyName,
        location: employerDetailsState.employerDetails?.location
      }
    })

    if (includeTimeInSituationFields && employerDetailsState.lengthOfTime) {
      dispatch({
        type: 'UPDATE_TIME_IN_SITUATION',
        value: employerDetailsState.lengthOfTime
      })
    }
  }

  const nextHandler = () => {
    if (!validate()) {
      resetNextButtonCb()
      return
    }
    saveValues()
    dispatch({
      type: 'NEXT'
    })
  }

  const previousHandler = () => {
    saveValues()
    appFormContextDispatch({
      type: 'NAVIGATED_FROM_QUESTION',
      questionName: `empDetails-${parseInt(entryId, 10)}`,
      lastNavAction: 'PREVIOUS'
    })
    dispatch({
      type: 'PREVIOUS'
    })
  }

  const handleEnterPress = (event: React.KeyboardEvent) => {
    if (event.code === 'Enter' || event.key === 'Enter') {
      nextButtonCb(nextHandler)
    }
  }

  const renderQuestion = (): JSX.Element => (
    <>
      <MuiTextField
        aria-label="label-jobTitle"
        data-hj-suppress=""
        defaultValue={getEmployeeDetailsValues('jobTitle')}
        errorMessage={errors?.jobTitle}
        id="jobTitle"
        inputMaxLength={100}
        inputType="text"
        inputVariant="outlined"
        key={entryId}
        label="Job title"
        mb="34px"
        mt="12px"
        multipleInputs
        name="jobTitle"
        onChangeHandler={updateEmployerDetailsState}
        testId="job-title-input"
      />
      <MuiTextField
        aria-label="label-companyName"
        data-hj-suppress=""
        defaultValue={getEmployeeDetailsValues('companyName')}
        errorMessage={errors?.companyName}
        id="companyName"
        inputMaxLength={100}
        inputType="text"
        inputVariant="outlined"
        label="Company name"
        mb="34px"
        multipleInputs
        name="companyName"
        onChangeHandler={updateEmployerDetailsState}
        testId="company-name-input"
      />
    </>
  )

  return (
    <>
      <AppFormQuestion
        questionKey={`${QuestionKey.EMPLOYMENT_HISTORY}.employer`}
        mt={3}
        mb={2}
        hintText={'This information helps our lenders understand what type of employment you are in and your ability to repay the finance.'}
        textStyle={questionFontStyle}
      >
        {`Can you tell us about your ${entryId !== '1' ? 'previous ' : ''}employment?`}
      </AppFormQuestion>

      {renderQuestion()}

      {/* TODO This block will be removed when removing reorderQuestions, as includeTimeInSituationFields wont be passed, unless employmentRedesign wins */}
      {includeTimeInSituationFields && (
        <GridItem mb="34px">
          <LengthOfTime
            valueChanged={updateLengthOfTimeState}
            onKeyPress={handleEnterPress}
            defaultValue={employerDetailsState.lengthOfTime}
            error={lengthOfTimeErrors()}
          >
            <AppFormQuestion
              textStyle="material.body.medium.smallLineHeight"
              mt={0}
              mb={12}
              questionKey={`${QuestionKey.EMPLOYMENT_HISTORY}.employerDetails.lengthOfTime`}
            >
              {entryId !== '1' ? 'How long did you work there?' : 'How long have you worked here?'}
            </AppFormQuestion>
          </LengthOfTime>
        </GridItem>
      )}
      <NavigationButtons
        nextHandler={() => nextButtonCb(nextHandler)}
        previousHandler={() => previousButtonCb(previousHandler)}
        disableNext={!(hasEnoughInfoToProceed())}
        disablePrevious={disablePrevious ? 'invisible' : undefined}
      />
    </>
  )
}

export default withFade(EmployerDetails)
